import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'pending' | 'unknown'>('unknown');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get payment parameters from URL
        const paymentId = searchParams.get('payment_id');
        const subscriptionId = searchParams.get('subscription_id');
        const status = searchParams.get('status');
        
        if (status === 'failed') {
          setPaymentStatus('failed');
          setLoading(false);
          return;
        }
        
        if (status === 'success' || status === 'completed') {
          setPaymentStatus('success');
          setLoading(false);
          return;
        }
        
        // If no status in URL, try to verify with backend
        if (paymentId) {
          // First try to verify using payment ID (most accurate)
          try {
            const response = await api.get(`/subscriptions/payment-status/${paymentId}`);
            const paymentData = response.data;
            
            if (paymentData.status === 'succeeded' || paymentData.status === 'completed') {
              setPaymentStatus('success');
            } else if (paymentData.status === 'failed' || paymentData.status === 'cancelled') {
              setPaymentStatus('failed');
            } else if (paymentData.status === 'pending') {
              setPaymentStatus('pending');
            } else {
              setPaymentStatus('unknown');
            }
          } catch (err) {
            console.error('Failed to verify payment by ID:', err);
            // Fallback to subscription verification
            if (subscriptionId) {
              try {
                const response = await api.get(`/subscriptions/${subscriptionId}`);
                const subscription = response.data;
                
                if (subscription.status === 'ACTIVE') {
                  setPaymentStatus('success');
                } else if (subscription.status === 'UNPAID' || subscription.status === 'FAILED') {
                  setPaymentStatus('failed');
                } else {
                  setPaymentStatus('pending');
                }
              } catch (subErr) {
                console.error('Failed to verify subscription:', subErr);
                setError('Unable to verify payment status');
                setPaymentStatus('unknown');
              }
            } else {
              setError('Unable to verify payment status');
              setPaymentStatus('unknown');
            }
          }
        } else if (subscriptionId) {
          // Fallback to subscription verification if no payment ID
          try {
            const response = await api.get(`/subscriptions/${subscriptionId}`);
            const subscription = response.data;
            
            if (subscription.status === 'ACTIVE') {
              setPaymentStatus('success');
            } else if (subscription.status === 'UNPAID' || subscription.status === 'FAILED') {
              setPaymentStatus('failed');
            } else {
              setPaymentStatus('pending');
            }
          } catch (err) {
            console.error('Failed to verify subscription:', err);
            setError('Unable to verify payment status');
            setPaymentStatus('unknown');
          }
        } else {
          setError('No payment or subscription information found');
          setPaymentStatus('unknown');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Failed to verify payment');
        setPaymentStatus('unknown');
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToSubscriptions = () => {
    navigate('/subscriptions');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          Verifying your payment...
        </Typography>
      </Container>
    );
  }

  // Show different content based on payment status
  if (paymentStatus === 'failed') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ErrorIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
              Payment Failed
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Your payment could not be processed
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                We were unable to process your payment. Please try again or contact support if the problem persists.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<BackIcon />}
                onClick={handleGoToSubscriptions}
                size="large"
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                size="large"
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (paymentStatus === 'pending') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'warning.main' }}>
              Payment Pending
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Your payment is being processed
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                Your payment is currently being processed. You will receive a confirmation email once it's completed.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                size="large"
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (paymentStatus === 'unknown' || error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ErrorIcon 
              sx={{ 
                fontSize: 80, 
                color: 'warning.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'warning.main' }}>
              Payment Status Unknown
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Unable to verify payment status
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                {error || 'We could not verify your payment status. Please check your subscriptions or contact support.'}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<BackIcon />}
                onClick={handleGoToSubscriptions}
                size="large"
              >
                Check Subscriptions
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                size="large"
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Success case
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CheckIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main', 
              mb: 3 
            }} 
          />
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
            Payment Successful!
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for your subscription
          </Typography>
          
          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              Your payment has been processed successfully. You should receive a confirmation email shortly.
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              What happens next?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Your subscription is now active<br/>
              • Tokens have been added to your account<br/>
              • You can start using the enhanced features immediately
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              size="large"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BackIcon />}
              onClick={handleGoToSubscriptions}
              size="large"
            >
              Manage Subscriptions
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;
